import {
  RiAttachmentLine,
  RiAwardLine,
  RiBlurOffLine,
  RiBrushLine,
  RiBringToFront,
  RiCheckboxBlankLine,
  RiContrastDropLine,
  RiFileCopyLine,
  RiDeleteBin6Line,
  RiEyeOffLine,
  RiFocus3Line,
  RiGroupLine,
  RiImageLine,
  RiLockUnlockLine,
  RiPinDistanceLine,
  RiPushpinLine,
  RiSendToBack,
  RiShape2Line,
  RiStickyNoteLine,
  RiText,
  RiLink,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useCanvasStore } from '../canvas/canvasStore';
import { useStore } from '../state/store';
import {
  BRUSH_COLORS,
  MARKER_COLORS,
  NOTE_COLORS,
  PIN_COLORS,
  REDACT_COLORS,
  ROPE_COLORS,
  STAMP_COLORS,
  STAMP_PRESETS,
  TAPE_COLORS,
  type BrushProps,
  type CircleProps,
  type ConnectorProps,
  type FrameProps,
  type HighlightProps,
  type NoteColor,
  type NoteProps,
  type PhotoProps,
  type PinProps,
  type RedactProps,
  type RopeProps,
  type ShapeKind,
  type ShapeProps,
  type StampProps,
  type TapeProps,
  type TextProps,
} from '../canvas/core/types';
import { cn, shortId } from '@/lib/utils';

const TYPE_META: Record<string, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  note: { label: '情报卡片', icon: RiStickyNoteLine },
  shape: { label: '形状', icon: RiShape2Line },
  text: { label: '文本', icon: RiText },
  frame: { label: '分区框', icon: RiCheckboxBlankLine },
  connector: { label: '关联线', icon: RiLink },
  group: { label: '编组', icon: RiGroupLine },
  rope: { label: '联系绳索', icon: RiPinDistanceLine },
  pin: { label: '图钉', icon: RiPushpinLine },
  circle: { label: '圈注', icon: RiBlurOffLine },
  tape: { label: '胶带', icon: RiAttachmentLine },
  photo: { label: '照片', icon: RiImageLine },
  highlight: { label: '荧光标记', icon: RiContrastDropLine },
  brush: { label: '手绘标记', icon: RiBrushLine },
  stamp: { label: '印章', icon: RiAwardLine },
  redact: { label: '遮挡条', icon: RiEyeOffLine },
};

const SHAPE_LABEL: Record<string, string> = {
  rect: '矩形',
  rounded: '圆角',
  ellipse: '椭圆',
  diamond: '菱形',
  triangle: '三角',
  hexagon: '六边',
  star: '星形',
  arrow: '箭头',
};

const REF_LABEL: Record<string, string> = {
  target: '对象',
  entity: '线索',
  evidence: '证据',
  hypothesis: '研判',
};

export function ElementInspector() {
  const controller = useCanvasStore(state => state.controller);
  const revision = useCanvasStore(state => state.revision);
  const entities = useStore(state => state.entities);
  const selectTarget = useStore(state => state.selectTarget);

  void revision;
  if (!controller) return null;

  const selected = controller.selection.elements;

  const patch = (changes: Record<string, unknown>, propsPatch?: Record<string, unknown>) => {
    for (const el of selected) {
      controller.surface.update(el.id, {
        ...changes,
        ...(propsPatch ? { props: { ...(el.props as Record<string, unknown>), ...propsPatch } } : {}),
      });
    }
    controller.notifyChange();
  };

  if (selected.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <span className="label-caps">关联图元素</span>
        <div className="rounded-[var(--radius)] border border-dashed border-border p-4 text-[11.5px] leading-relaxed text-muted-foreground">
          未选中元素。在关联图中点选卡片或连线以查看与编辑属性。
        </div>
        <div className="flex flex-col gap-1.5 rounded-[var(--radius)] border border-border bg-muted/40 p-2.5 text-[10.5px] text-muted-foreground">
          <Shortcut keys="双击空白" desc="新建情报卡片" />
          <Shortcut keys="空格 + 拖拽" desc="平移画布" />
          <Shortcut keys="滚轮" desc="缩放" />
          <Shortcut keys="Ctrl + 1" desc="适应全图" />
          <Shortcut keys="Ctrl + G" desc="编组 / 解组" />
        </div>
      </div>
    );
  }

  if (selected.length > 1) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <span className="label-caps">已选 {selected.length} 个元素</span>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" onClick={() => controller.groupSelection()}>
            <RiGroupLine className="size-3.5" />
            编组
          </Button>
          <Button size="sm" variant="outline" onClick={() => controller.ungroupSelection()}>
            <RiLockUnlockLine className="size-3.5" />
            解组
          </Button>
          <Button size="sm" variant="outline" onClick={() => controller.duplicateSelection()}>
            <RiFileCopyLine className="size-3.5" />
            复制
          </Button>
          <Button size="sm" variant="destructive" onClick={() => controller.deleteSelection()}>
            <RiDeleteBin6Line className="size-3.5" />
            删除
          </Button>
        </div>
      </div>
    );
  }

  const el = selected[0];
  const meta = TYPE_META[el.type] ?? { label: el.type, icon: RiShape2Line };
  const Icon = meta.icon;
  const linkedEntity = el.refKind === 'entity' ? entities.find(e => e.id === el.refId) : undefined;

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-center gap-2">
        <div className="grid size-6 place-items-center rounded-[5px] bg-muted">
          <Icon className="size-3.5" />
        </div>
        <span className="text-[12px] font-semibold">{meta.label}</span>
        <span className="ml-auto font-mono text-[9.5px] text-muted-foreground">{shortId(el.id, 10)}</span>
      </div>

      {(el.refKind || el.refId) && (
        <div className="flex flex-col gap-1.5 rounded-[var(--radius)] border border-border bg-muted/40 p-2.5">
          <span className="label-caps">关联对象</span>
          <div className="flex items-center gap-1.5 text-[11.5px]">
            <Badge variant={el.refKind === 'target' ? 'default' : 'lead'}>{REF_LABEL[el.refKind ?? ''] ?? '手动'}</Badge>
            <span className="truncate">{linkedEntity?.value ?? linkedEntity?.label ?? el.refId}</span>
          </div>
          {linkedEntity?.targetId && (
            <Button size="xs" variant="ghost" className="self-start px-0" onClick={() => selectTarget(linkedEntity.targetId)}>
              <RiFocus3Line className="size-3" />
              定位到对象档案
            </Button>
          )}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <span className="label-caps">位置与尺寸</span>
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="X" value={Math.round(el.x)} onChange={v => patch({ x: v })} />
          <NumberField label="Y" value={Math.round(el.y)} onChange={v => patch({ y: v })} />
          <NumberField label="宽" value={Math.round(el.w)} onChange={v => patch({ w: Math.max(20, v) })} />
          <NumberField label="高" value={Math.round(el.h)} onChange={v => patch({ h: Math.max(20, v) })} />
          <NumberField label="旋转" value={Math.round(el.rotate)} onChange={v => patch({ rotate: v })} />
        </div>
      </div>

      {el.type === 'note' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">卡片内容</span>
          <Input
            value={(el.props as unknown as NoteProps).title ?? ''}
            placeholder="标题"
            onChange={e => patch({}, { title: e.target.value })}
          />
          <Textarea
            rows={5}
            value={(el.props as unknown as NoteProps).text ?? ''}
            onChange={e => patch({}, { text: e.target.value })}
          />
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(NOTE_COLORS) as NoteColor[]).map(color => (
              <button
                key={color}
                type="button"
                title={color}
                onClick={() => patch({}, { color })}
                className={cn(
                  'size-5 rounded-full border transition-transform',
                  (el.props as unknown as NoteProps).color === color
                    ? 'scale-110 border-foreground'
                    : 'border-border hover:scale-105'
                )}
                style={{ background: NOTE_COLORS[color].accent }}
              />
            ))}
          </div>
        </div>
      )}

      {el.type === 'shape' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">形状</span>
          <div className="grid grid-cols-4 gap-1">
            {(['rect', 'rounded', 'ellipse', 'diamond', 'triangle', 'hexagon', 'star', 'arrow'] as ShapeKind[]).map(kind => (
              <button
                key={kind}
                type="button"
                onClick={() => patch({}, { shape: kind })}
                className={cn(
                  'rounded-[var(--radius-sm)] border py-1 text-[10.5px]',
                  (el.props as unknown as ShapeProps).shape === kind
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {SHAPE_LABEL[kind]}
              </button>
            ))}
          </div>
          <Input
            value={(el.props as unknown as ShapeProps).text ?? ''}
            placeholder="形状内文本"
            onChange={e => patch({}, { text: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <ColorField
              label="填充"
              value={(el.props as unknown as ShapeProps).fill}
              onChange={v => patch({}, { fill: v })}
            />
            <ColorField
              label="描边"
              value={(el.props as unknown as ShapeProps).stroke}
              onChange={v => patch({}, { stroke: v })}
            />
          </div>
        </div>
      )}

      {el.type === 'text' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">文本</span>
          <Textarea
            rows={3}
            value={(el.props as unknown as TextProps).text ?? ''}
            onChange={e => patch({}, { text: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <NumberField
              label="字号"
              value={(el.props as unknown as TextProps).fontSize}
              onChange={v => patch({}, { fontSize: v })}
            />
            <ColorField
              label="颜色"
              value={(el.props as unknown as TextProps).color}
              onChange={v => patch({}, { color: v })}
            />
          </div>
          <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              checked={Boolean((el.props as unknown as TextProps).bold)}
              onChange={e => patch({}, { bold: e.target.checked })}
            />
            加粗
          </label>
        </div>
      )}

      {el.type === 'frame' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">分组框</span>
          <Input
            value={(el.props as unknown as FrameProps).title ?? ''}
            onChange={e => patch({}, { title: e.target.value })}
          />
          <ColorField
            label="颜色"
            value={(el.props as unknown as FrameProps).color}
            onChange={v => patch({}, { color: v })}
          />
        </div>
      )}

      {el.type === 'connector' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">关联线</span>
          <div className="grid grid-cols-2 gap-1">
            {(['orth', 'straight'] as const).map(mode => (
              <button
                key={mode}
                type="button"
                onClick={() => {
                  controller.surface.update(el.id, {
                    props: { ...(el.props as Record<string, unknown>), mode },
                  });
                  controller.notifyChange();
                }}
                className={cn(
                  'rounded-[var(--radius-sm)] border py-1 text-[10.5px]',
                  (el.props as unknown as ConnectorProps).mode === mode
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {mode === 'orth' ? '折线' : '直线'}
              </button>
            ))}
          </div>
          <Input
            value={(el.props as unknown as ConnectorProps).label ?? ''}
            placeholder="关系标签，如 任职于 / 拥有"
            onChange={e => patch({}, { label: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <ColorField
              label="颜色"
              value={(el.props as unknown as ConnectorProps).stroke}
              onChange={v => patch({}, { stroke: v })}
            />
            <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={Boolean((el.props as unknown as ConnectorProps).dashed)}
                onChange={e => patch({}, { dashed: e.target.checked })}
              />
              虚线
            </label>
          </div>
        </div>
      )}

      {el.type === 'rope' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">联系绳索</span>
          <Swatches
            label="颜色"
            colors={ROPE_COLORS}
            value={(el.props as unknown as RopeProps).color}
            onChange={v => patch({}, { color: v })}
          />
          <div className="grid grid-cols-2 gap-2">
            <NumberField
              label="粗细"
              value={(el.props as unknown as RopeProps).width}
              onChange={v => patch({}, { width: Math.max(0.5, v) })}
            />
            <NumberField
              label="下垂（%）"
              value={Math.round(((el.props as unknown as RopeProps).sag ?? 0.14) * 100)}
              onChange={v => patch({}, { sag: Math.max(0, v) / 100 })}
            />
          </div>
          <Input
            value={(el.props as unknown as RopeProps).label ?? ''}
            placeholder="关系标签"
            onChange={e => patch({}, { label: e.target.value })}
          />
        </div>
      )}

      {el.type === 'pin' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">图钉</span>
          <Swatches
            label="颜色"
            colors={PIN_COLORS}
            value={(el.props as unknown as PinProps).color}
            onChange={v => patch({}, { color: v })}
          />
          <div className="grid grid-cols-3 gap-1">
            {(['push', 'clip', 'nail'] as const).map(kind => (
              <button
                key={kind}
                type="button"
                onClick={() => patch({}, { kind })}
                className={cn(
                  'rounded-[var(--radius-sm)] border py-1 text-[10.5px]',
                  (el.props as unknown as PinProps).kind === kind
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {kind === 'push' ? '图钉' : kind === 'clip' ? '回形针' : '钉子'}
              </button>
            ))}
          </div>
        </div>
      )}

      {el.type === 'circle' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">圈注</span>
          <Swatches
            label="颜色"
            colors={ROPE_COLORS}
            value={(el.props as unknown as CircleProps).color}
            onChange={v => patch({}, { color: v })}
          />
          <div className="grid grid-cols-2 gap-2">
            <NumberField
              label="线宽"
              value={(el.props as unknown as CircleProps).strokeWidth}
              onChange={v => patch({}, { strokeWidth: Math.max(0.5, v) })}
            />
            <label className="flex items-end gap-2 pb-1.5 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={Boolean((el.props as unknown as CircleProps).dashed)}
                onChange={e => patch({}, { dashed: e.target.checked })}
              />
              虚线
            </label>
          </div>
          <Input
            value={(el.props as unknown as CircleProps).text ?? ''}
            placeholder="圈注文字"
            onChange={e => patch({}, { text: e.target.value })}
          />
          <Button size="xs" variant="outline" onClick={() => patch({}, { seed: Math.floor(Math.random() * 100000) + 1 })}>
            重绘抖动
          </Button>
        </div>
      )}

      {el.type === 'tape' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">胶带</span>
          <Swatches
            label="颜色"
            colors={TAPE_COLORS}
            value={(el.props as unknown as TapeProps).color}
            onChange={v => patch({}, { color: v })}
          />
        </div>
      )}

      {el.type === 'highlight' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">荧光标记</span>
          <Swatches
            label="颜色"
            colors={MARKER_COLORS}
            value={(el.props as unknown as HighlightProps).color}
            onChange={v => patch({}, { color: v })}
          />
        </div>
      )}

      {el.type === 'redact' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">遮挡条</span>
          <Swatches
            label="颜色"
            colors={REDACT_COLORS}
            value={(el.props as unknown as RedactProps).color}
            onChange={v => patch({}, { color: v })}
          />
        </div>
      )}

      {el.type === 'photo' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">照片</span>
          <div className="overflow-hidden rounded-[var(--radius-sm)] border border-border">
            <img src={(el.props as unknown as PhotoProps).src} alt="" className="h-36 w-full object-cover" />
          </div>
          <Input
            value={(el.props as unknown as PhotoProps).caption ?? ''}
            placeholder="照片说明"
            onChange={e => patch({}, { caption: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-1">
            {(['polaroid', 'plain'] as const).map(frame => (
              <button
                key={frame}
                type="button"
                onClick={() => patch({}, { frame })}
                className={cn(
                  'rounded-[var(--radius-sm)] border py-1 text-[10.5px]',
                  (el.props as unknown as PhotoProps).frame === frame
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {frame === 'polaroid' ? '宝丽来' : '无边框'}
              </button>
            ))}
          </div>
          <label className="cursor-pointer self-start">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => patch({}, { src: String(reader.result ?? '') });
                reader.readAsDataURL(file);
              }}
            />
            <span className="inline-flex h-7 items-center justify-center gap-1.5 rounded-[var(--radius-sm)] border border-input bg-card px-2.5 text-[12px] hover:bg-accent">
              <RiImageLine className="size-3.5" />
              替换图片
            </span>
          </label>
        </div>
      )}

      {el.type === 'brush' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">手绘标记</span>
          <div className="grid grid-cols-2 gap-1">
            {(['pencil', 'marker'] as const).map(mode => (
              <button
                key={mode}
                type="button"
                onClick={() => patch({}, { mode })}
                className={cn(
                  'rounded-[var(--radius-sm)] border py-1 text-[10.5px]',
                  (el.props as unknown as BrushProps).mode === mode
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {mode === 'pencil' ? '铅笔' : '马克笔'}
              </button>
            ))}
          </div>
          <Swatches
            label="颜色"
            colors={BRUSH_COLORS}
            value={(el.props as unknown as BrushProps).color}
            onChange={v => patch({}, { color: v })}
          />
          <NumberField
            label="笔粗"
            value={(el.props as unknown as BrushProps).width}
            onChange={v => patch({}, { width: Math.max(0.5, v) })}
          />
          <div className="text-[10px] text-muted-foreground">点数 {(el.props as unknown as BrushProps).points?.length ?? 0}</div>
        </div>
      )}

      {el.type === 'stamp' && (
        <div className="flex flex-col gap-2">
          <span className="label-caps">印章</span>
          <div className="flex flex-wrap gap-1">
            {STAMP_PRESETS.map(text => (
              <button
                key={text}
                type="button"
                onClick={() => patch({}, { text })}
                className={cn(
                  'rounded-[var(--radius-sm)] border px-2 py-1 text-[10.5px]',
                  (el.props as unknown as StampProps).text === text
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {text}
              </button>
            ))}
          </div>
          <Input
            value={(el.props as unknown as StampProps).text ?? ''}
            placeholder="印章文字"
            onChange={e => patch({}, { text: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-1">
            {(['round', 'rect'] as const).map(shape => (
              <button
                key={shape}
                type="button"
                onClick={() => patch({}, { shape })}
                className={cn(
                  'rounded-[var(--radius-sm)] border py-1 text-[10.5px]',
                  (el.props as unknown as StampProps).shape === shape
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {shape === 'round' ? '圆章' : '方章'}
              </button>
            ))}
          </div>
          <Swatches
            label="颜色"
            colors={STAMP_COLORS}
            value={(el.props as unknown as StampProps).color}
            onChange={v => patch({}, { color: v })}
          />
          <Button size="xs" variant="outline" onClick={() => patch({}, { seed: Math.floor(Math.random() * 100000) + 1 })}>
            重新做旧
          </Button>
        </div>
      )}

      {(el.type === 'pin' || el.type === 'tape' || el.type === 'photo' || el.type === 'brush' || el.type === 'circle' || el.type === 'stamp' || el.type === 'highlight' || el.type === 'redact') && (
        <div className="flex flex-col gap-1.5 rounded-[var(--radius-sm)] border border-border bg-muted/40 p-2">
          <span className="label-caps">随附</span>
          {el.attachTo ? (
            <div className="flex items-center gap-2 text-[11px]">
              <Badge variant="evidence">已固定到宿主</Badge>
              <span className="truncate font-mono text-[10px] text-muted-foreground">{shortId(el.attachTo, 10)}</span>
              <button
                type="button"
                className="ml-auto text-[10.5px] text-primary hover:underline"
                onClick={() => controller.surface.update(el.id, { attachTo: undefined })}
              >
                解除
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <span className="text-[10.5px] text-muted-foreground">选择一张卡片，让它随卡片一起移动</span>
              <select
                className="h-7 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[11px]"
                defaultValue=""
                onChange={event => {
                  if (event.target.value) controller.surface.update(el.id, { attachTo: event.target.value });
                }}
              >
                <option value="">选择宿主卡片…</option>
                {controller.surface
                  .all()
                  .filter(candidate => candidate.type === 'note' && candidate.id !== el.id)
                  .slice(0, 80)
                  .map(candidate => (
                    <option key={candidate.id} value={candidate.id}>
                      {(candidate.props as unknown as NoteProps).title ?? candidate.id.slice(0, 8)}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 border-t border-border pt-2.5">
        <Button size="xs" variant="outline" onClick={() => controller.bringToFront()}>
          <RiBringToFront className="size-3" />
          置顶
        </Button>
        <Button size="xs" variant="outline" onClick={() => controller.sendToBack()}>
          <RiSendToBack className="size-3" />
          置底
        </Button>
        <Button size="xs" variant="outline" onClick={() => controller.duplicateSelection()}>
          <RiFileCopyLine className="size-3" />
          复制
        </Button>
        <Button size="xs" variant="destructive" onClick={() => controller.deleteSelection()}>
          <RiDeleteBin6Line className="size-3" />
          删除
        </Button>
      </div>
    </div>
  );
}

function Shortcut({ keys, desc }: { keys: string; desc: string }) {
  return (
    <div className="flex items-center gap-2">
      <kbd className="rounded border border-border bg-card px-1.5 py-px font-mono text-[9.5px]">{keys}</kbd>
      <span>{desc}</span>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <Input
        type="number"
        className="h-7"
        value={Number.isFinite(value) ? value : 0}
        onChange={e => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <input
        type="color"
        className="h-7 w-full cursor-pointer rounded-[var(--radius-sm)] border border-input bg-card p-0.5"
        value={value ?? '#475569'}
        onChange={e => onChange(e.target.value)}
      />
    </label>
  );
}

function Swatches({
  label,
  colors,
  value,
  onChange,
}: {
  label: string;
  colors: string[];
  value: string | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {colors.map(color => (
          <button
            key={color}
            type="button"
            title={color}
            onClick={() => onChange(color)}
            className={cn(
              'size-5 rounded-full border transition-transform',
              value === color ? 'scale-110 border-foreground' : 'border-border hover:scale-105'
            )}
            style={{ background: color }}
          />
        ))}
      </div>
    </div>
  );
}